/**
 * JoinFlow — four-step joiner stepper used at /game/:sessionId/lobby.
 *
 *   1. Name entry           (2-16 chars, ASCII + Turkish diacritics)
 *   2. Colour pick          (6 historical-pigment swatches)
 *   3. READY rubber stamp   (chalk-stamp ChalkButton)
 *   4. Waiting view         ("Waiting for game to start…")
 *
 * Steps 1 and 2 hold their values in local component state without
 * touching the server — we don't want a roster slot appearing for a
 * half-typed name. On the READY stamp (step 3) we issue the actual
 * `joinSession` and `toggleReady` dispatches; the next `lobbyState`
 * broadcast then reveals our slot to everyone.
 */

import { useMemo, useState, type ChangeEvent } from 'react';
import { surfaces, fontStacks, playerColors } from '@qd/design-tokens';
import {
  PLAYER_COLORS,
  PLAYER_NAME_REGEX,
  type PlayerColorKey,
} from '@qd/protocol';
import { ChalkText } from '../base/ChalkText';
import { ChalkButton } from '../base/ChalkButton';
import {
  useLobbyStore,
  usePlayers,
  useTakenColors,
  useWsState,
} from '../../stores/lobbyStore';

interface JoinFlowProps {
  sessionId: string;
}

type Step = 'name' | 'color' | 'ready' | 'waiting';

export function JoinFlow({ sessionId }: JoinFlowProps) {
  const [step, setStep] = useState<Step>('name');
  const [name, setName] = useState('');
  const [color, setColor] = useState<PlayerColorKey | null>(null);

  const players = usePlayers();
  const takenColors = useTakenColors();
  const joinSession = useLobbyStore((s) => s.joinSession);
  const toggleReady = useLobbyStore((s) => s.toggleReady);
  const wsState = useWsState();
  const isConnected = wsState === 'open';

  function commitName() {
    if (!PLAYER_NAME_REGEX.test(name.trim())) return;
    setStep('color');
  }

  function commitColor(c: PlayerColorKey) {
    if (takenColors.has(c)) return;
    setColor(c);
    setStep('ready');
  }

  function commitReady() {
    if (!color) return;
    // Tell the server who we are. The server replies with a `lobbyState`
    // broadcast that adds our slot to the roster for everyone.
    joinSession(name.trim(), color);
    // Then flip our ready flag. The server handles the toggle in a
    // separate frame; the UI reads the resulting `isReady` from the
    // lobbyState rather than tracking it locally.
    toggleReady();
    setStep('waiting');
  }

  // If we navigate away and back with a still-open WS, the lobbyState
  // already contains our slot and we should land directly in waiting.
  const alreadyJoined = useMemo(() => {
    return players.some((p) => p.name.toLowerCase() === name.trim().toLowerCase() && p.isReady);
  }, [players, name]);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 520,
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        alignItems: 'center',
      }}
    >
      <Stepper step={step} />

      {step === 'name' && (
        <NameStep value={name} onChange={setName} onSubmit={commitName} />
      )}
      {step === 'color' && (
        <ColorStep
          taken={takenColors}
          selected={color}
          onPick={commitColor}
        />
      )}
      {step === 'ready' && (
        <ReadyStep
          name={name}
          color={color}
          isConnected={isConnected}
          onStamp={commitReady}
          onBack={() => setStep('color')}
        />
      )}
      {(step === 'waiting' || alreadyJoined) && <WaitingStep sessionId={sessionId} />}
    </div>
  );
}

interface StepperProps {
  step: Step;
}
function Stepper({ step }: StepperProps) {
  const order: Step[] = ['name', 'color', 'ready', 'waiting'];
  const idx = order.indexOf(step);
  return (
    <div
      role="progressbar"
      aria-valuenow={idx + 1}
      aria-valuemin={1}
      aria-valuemax={4}
      style={{
        display: 'flex',
        gap: 8,
        fontFamily: fontStacks.mono,
        fontSize: 11,
        letterSpacing: '0.32em',
        color: surfaces.chalk,
        opacity: 0.75,
      }}
    >
      {order.map((s, i) => (
        <span key={s} style={{ opacity: i <= idx ? 1 : 0.4 }}>
          {String(i + 1).padStart(2, '0')} {s.toUpperCase()}
        </span>
      ))}
    </div>
  );
}

interface NameStepProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}
function NameStep({ value, onChange, onSubmit }: NameStepProps) {
  const valid = PLAYER_NAME_REGEX.test(value.trim());
  return (
    <>
      <ChalkText size="heading" underline>
        Enter Your Name
      </ChalkText>
      <input
        autoFocus
        value={value}
        maxLength={16}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid) onSubmit();
        }}
        placeholder="ALI"
        style={{
          background: surfaces.paper,
          color: surfaces.ink,
          fontFamily: fontStacks.mono,
          fontSize: 32,
          letterSpacing: '0.18em',
          padding: '14px 22px',
          border: `2px solid ${surfaces.chalk}`,
          borderRadius: 4,
          width: '100%',
          maxWidth: 320,
          textAlign: 'center',
          textTransform: 'uppercase',
          outline: 'none',
        }}
      />
      <small
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 12,
          color: surfaces.chalk,
          opacity: 0.45,
          letterSpacing: '0.18em',
          textAlign: 'center',
        }}
      >
        2–16 chars · letters, numbers, spaces, dashes
      </small>
      <ChalkButton variant="primary" size="lg" disabled={!valid} onClick={onSubmit}>
        Continue
      </ChalkButton>
    </>
  );
}

interface ColorStepProps {
  taken: ReadonlySet<PlayerColorKey>;
  selected: PlayerColorKey | null;
  onPick: (c: PlayerColorKey) => void;
}
function ColorStep({ taken, selected, onPick }: ColorStepProps) {
  return (
    <>
      <ChalkText size="heading" underline>
        Pick Your Pigment
      </ChalkText>
      <div
        role="radiogroup"
        aria-label="Player colour"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(80px, 1fr))',
          gap: 16,
          width: '100%',
          maxWidth: 360,
        }}
      >
        {PLAYER_COLORS.map((c) => {
          const meta = playerColors[c];
          const isTaken = taken.has(c) && c !== selected;
          const isSelected = c === selected;
          return (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${meta.name}${isTaken ? ' (taken)' : ''}`}
              disabled={isTaken}
              onClick={() => onPick(c)}
              style={{
                position: 'relative',
                background: meta.hex,
                border: `2px solid ${
                  isSelected ? surfaces.chalk : `${surfaces.chalk}55`
                }`,
                borderRadius: 4,
                aspectRatio: '1',
                cursor: isTaken ? 'not-allowed' : 'pointer',
                color: surfaces.paper,
                fontFamily: fontStacks.mono,
                fontSize: 11,
                letterSpacing: '0.18em',
                padding: 0,
                overflow: 'hidden',
                opacity: isTaken ? 0.55 : 1,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  bottom: 6,
                  left: 0,
                  right: 0,
                  textTransform: 'uppercase',
                  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                }}
              >
                {meta.name.split(' ')[0]}
              </span>
              {isTaken && (
                <svg
                  aria-hidden
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <line
                    x1="6"
                    y1="6"
                    x2="94"
                    y2="94"
                    stroke={surfaces.chalk}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

interface ReadyStepProps {
  name: string;
  color: PlayerColorKey | null;
  isConnected: boolean;
  onStamp: () => void;
  onBack: () => void;
}
function ReadyStep({ name, color, isConnected, onStamp, onBack }: ReadyStepProps) {
  return (
    <>
      <ChalkText size="heading" underline>
        Stamp In, {name.toUpperCase() || 'PLAYER'}
      </ChalkText>
      <p
        style={{
          fontFamily: fontStacks.body,
          fontSize: 16,
          color: surfaces.chalk,
          opacity: 0.7,
          textAlign: 'center',
          maxWidth: 380,
        }}
      >
        Press READY to mark yourself as ready. The lead tester will start
        the match when the roster is full.
      </p>
      {color != null && (
        <span
          style={{
            display: 'inline-block',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: playerColors[color].hex,
            border: `2px solid ${surfaces.chalk}`,
          }}
        />
      )}
      <div style={{ display: 'flex', gap: 16 }}>
        <ChalkButton variant="secondary" onClick={onBack}>
          Back
        </ChalkButton>
        {/* Gate the stamp on transport state — clicks issued while the
            socket is reconnecting silently drop into the wsClient queue
            but feel like a frozen UI from the user's perspective. The
            ReconnectOverlay surfaces the global "Reconnecting" pill;
            here we make the action visibly disabled so a tap on a stale
            tab doesn't read as broken. */}
        <ChalkButton
          variant="primary"
          size="lg"
          disabled={!isConnected}
          onClick={onStamp}
        >
          {isConnected ? 'Ready' : 'Reconnecting…'}
        </ChalkButton>
      </div>
    </>
  );
}

function WaitingStep({ sessionId }: { sessionId: string }) {
  return (
    <>
      <ChalkText size="heading" underline>
        Waiting for game to start…
      </ChalkText>
      <p
        style={{
          fontFamily: fontStacks.body,
          fontSize: 15,
          color: surfaces.chalk,
          opacity: 0.7,
          textAlign: 'center',
          maxWidth: 360,
        }}
      >
        Hang tight. The lobby owner will press BEGIN MATCH when the
        roster is set.
      </p>
      <span
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 12,
          letterSpacing: '0.22em',
          color: surfaces.chalk,
          opacity: 0.4,
        }}
      >
        SESSION · {sessionId.slice(0, 14)}
      </span>
    </>
  );
}
